import { useCallback, useState } from "react";
import { Upload } from "lucide-react";
import { toast } from "sonner";
import {
  apiDelete,
  apiPost,
  isNetworkError,
  queryClient,
  retryOnceOnNetwork,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// D2j — client-side upload lifecycle. Post-upload transitions
// (processing → ready → confirmed) are derived from the server tray query
// inside ReceiptTray; we only track what we own here: before-server-knows
// (uploading), server-knows-but-OCR-pending (uploaded), and the upload-side
// failure mode (error).
export type FileUploadStatus =
  | "converting"
  | "uploading"
  | "uploaded"
  | "processing"
  | "processed"
  | "confirmed"
  | "error";

// Image formats OpenAI's vision API accepts directly (input_image content
// type for gpt-4.1-mini): PNG, JPEG, WebP, non-animated GIF. Plus PDF, which
// the backend routes to input_file. These pass through to S3 unchanged.
const PASSTHROUGH_RE = /\.(jpe?g|png|webp|gif|pdf)$/i;
// Apple's HEIC/HEIF format. Browsers can't render it (except Safari), and
// OpenAI rejects it as input_image. We convert it to JPEG client-side via
// heic2any (lazy-imported so non-HEIC users don't pay the ~700KB WASM cost).
const CONVERTIBLE_RE = /\.(heic|heif)$/i;
// Union for the file <input accept> attribute and the drag-and-drop filter.
// Accept attribute uses extensions; covers what OpenAI handles plus what we
// can convert client-side.
const ACCEPT_ATTR = ".jpg,.jpeg,.png,.webp,.gif,.pdf,.heic,.heif";

export interface UploadedFile {
  // Stable client-side key (a tempId). Never changes once assigned so React
  // keys are stable and the state-update predicates always match.
  id: string;
  // Server-side receipt id. Set once the /upload endpoint responds; used by
  // ReceiptTray to join the client in-flight rows with the server tray query.
  receiptId?: string;
  name: string;
  status: FileUploadStatus;
  // File retained so the accountant can retry the upload in place without
  // re-picking from disk. Kept even on error for the retry button.
  file?: File;
  previewUrl?: string;
}

// Convert a HEIC/HEIF file to a JPEG File. heic2any is lazy-imported so the
// ~700KB libheif WASM only ships to users who actually drop a HEIC. Returns
// a new File whose name has the original base + ".jpg" so the backend's
// extension-based PDF detection (and the HEIC reject) keep working.
async function convertHeicToJpeg(file: File): Promise<File> {
  const { default: heic2any } = await import("heic2any");
  const out = await heic2any({
    blob: file,
    toType: "image/jpeg",
    quality: 0.85,
  });
  // heic2any returns Blob | Blob[] (array for multi-frame HEIC). Receipts
  // are single-image so take the first frame.
  const blob = Array.isArray(out) ? out[0] : out;
  const jpegName = file.name.replace(CONVERTIBLE_RE, ".jpg");
  return new File([blob], jpegName, { type: "image/jpeg" });
}

// Shared uploader. Used both by <BulkUpload> for freshly dropped files and by
// <ReceiptTray> for retrying an errored row without forcing the accountant to
// re-pick the file.
//
// Lifecycle + the "ghost receipt" fix: /upload creates a Django Receipt row
// before the S3 PUT happens. If the PUT fails (S3 CORS misconfig, network
// drop mid-PUT, 4xx on the presigned URL), that row has no file in S3 but
// still appears in the tray as `has_raw_data=false` → rendered as
// "Processing" forever. We DELETE that orphan on failure so a page refresh
// doesn't resurrect a stuck "processing" row the accountant can't act on.
export function useUploadFile(
  userId: string,
  setFiles: React.Dispatch<React.SetStateAction<UploadedFile[]>>,
) {
  return useCallback(
    async (file: File, reuseTempId?: string) => {
      const tempId = reuseTempId ?? crypto.randomUUID();
      const needsConversion = CONVERTIBLE_RE.test(file.name);
      const initialStatus: FileUploadStatus = needsConversion
        ? "converting"
        : "uploading";

      if (reuseTempId) {
        // Retry path: reset the existing row back to its initial status and
        // clear any prior server id (a fresh /upload call will mint a new one).
        setFiles((prev) =>
          prev.map((f) =>
            f.id === tempId
              ? { ...f, status: initialStatus, receiptId: undefined, file }
              : f,
          ),
        );
      } else {
        setFiles((prev) => [
          ...prev,
          {
            id: tempId,
            // Show the original filename even for HEIC; we'll silently rename
            // to .jpg post-conversion so the user-visible label stays familiar
            // (their iPhone's "IMG_0021.HEIC", not a synthetic name).
            name: file.name,
            status: initialStatus,
            file,
            // Skip the preview for HEIC — Chrome/Firefox can't render it, so
            // URL.createObjectURL would just produce a broken thumbnail. The
            // preview gets generated post-conversion below.
            previewUrl:
              !needsConversion && file.type.startsWith("image/")
                ? URL.createObjectURL(file)
                : undefined,
          },
        ]);
      }

      // Convert HEIC → JPEG before the server roundtrip. From here on,
      // `uploadFile_` refers to the converted version (or the original if no
      // conversion was needed). The user-visible row label keeps the ORIGINAL
      // filename (e.g. "IMG_0021.HEIC") because that's the anchor the
      // accountant has — they dropped a HEIC and they should see a HEIC.
      // Only the bytes-on-S3 and the file_name we send to the backend get
      // the .jpg suffix (so the backend's HEIC-reject validator and OpenAI's
      // vision API see a JPEG). Failure here surfaces in the same catch as
      // upload errors.
      let uploadFile_ = file;
      try {
        if (needsConversion) {
          uploadFile_ = await convertHeicToJpeg(file);
          // Generate a preview from the converted JPEG and flip status to
          // uploading so the row stops showing the converting spinner. Keep
          // `name` as the original — only `file` and `previewUrl` change.
          const previewUrl = URL.createObjectURL(uploadFile_);
          setFiles((prev) =>
            prev.map((f) =>
              f.id === tempId
                ? {
                    ...f,
                    status: "uploading",
                    file: uploadFile_,
                    previewUrl,
                  }
                : f,
            ),
          );
        }
      } catch (err) {
        setFiles((prev) =>
          prev.map((f) => (f.id === tempId ? { ...f, status: "error" } : f)),
        );
        toast.error(
          `Couldn't convert ${file.name}. ${
            err instanceof Error ? err.message : "Conversion failed."
          }`,
        );
        return;
      }

      let serverReceiptId: string | undefined;
      try {
        // upload_key dedupes the silent retryOnceOnNetwork retry on the
        // backend so a dropped response doesn't leave an orphan Receipt
        // stuck in "Processing" forever. tempId is already a per-file
        // crypto.randomUUID() reused across retries via this closure, so
        // it has the right lifetime to act as the idempotency key.
        const data = await retryOnceOnNetwork(() =>
          apiPost<{
            id: string;
            presigned_upload_url: string;
          }>(`/api/accountant/users/${userId}/receipts/upload`, {
            file_name: uploadFile_.name,
            upload_key: tempId,
          }),
        );
        serverReceiptId = data.id;

        // Attach the server receipt id now, while status is still
        // "uploading". The tray joins on receiptId and will skip firing
        // /process until status flips to "uploaded" below.
        setFiles((prev) =>
          prev.map((f) =>
            f.id === tempId ? { ...f, receiptId: data.id } : f,
          ),
        );

        // S3 PUT gets its own bounded retry — absorb a transient network blip
        // once, then surface. We don't retry 4xx (bad presigned URL, etc).
        const putOnce = async (): Promise<Response> => {
          try {
            return await fetch(data.presigned_upload_url, {
              method: "PUT",
              body: uploadFile_,
            });
          } catch {
            throw new Error("S3 upload network error");
          }
        };
        // First-attempt error gets preserved so that if the retry also
        // fails, the toast carries useful info instead of a generic message.
        let firstErr: Error | undefined;
        let putRes = await putOnce().catch((e: unknown) => {
          firstErr = e instanceof Error ? e : new Error(String(e));
          return null;
        });
        if (!putRes || putRes.status >= 500) {
          // First attempt failed with a network error or 5xx — retry once.
          await new Promise((r) => setTimeout(r, 500));
          try {
            putRes = await putOnce();
          } catch (e) {
            // Both attempts exploded. Surface the first error (closer to the
            // real cause) unless the retry is strictly more informative.
            throw firstErr ?? (e instanceof Error ? e : new Error(String(e)));
          }
        }
        if (!putRes.ok) throw new Error(`S3 upload ${putRes.status}`);

        // Hand off to the tray: it owns the /process call for every pending
        // receipt via its auto-resume effect. Invalidating the tray query
        // makes it refetch immediately so auto-resume fires within ~100ms.
        setFiles((prev) =>
          prev.map((f) =>
            f.id === tempId ? { ...f, status: "uploaded" } : f,
          ),
        );
        queryClient.invalidateQueries({
          queryKey: ["accountant-receipts-tray", userId],
        });
      } catch (err) {
        setFiles((prev) =>
          prev.map((f) => (f.id === tempId ? { ...f, status: "error" } : f)),
        );
        toast.error(
          isNetworkError(err)
            ? `Upload failed for ${uploadFile_.name}. Network is down.`
            : `Upload failed for ${uploadFile_.name}. ${
                err instanceof Error ? err.message : "Please try again."
              }`,
        );

        // Clean up the Django Receipt row we minted in /upload so it doesn't
        // linger as a ghost "processing" row on the next tray refresh. If
        // the PUT fails before we get a server id (first-call failure), there
        // is nothing to clean up. Best-effort — a DELETE that itself fails
        // leaves the orphan, but the accountant can Discard-all it.
        if (serverReceiptId) {
          apiDelete(
            `/api/accountant/users/${userId}/receipts/${serverReceiptId}`,
          )
            .catch(() => {
              // Silent — the toast above already surfaced the root cause.
            })
            .finally(() => {
              queryClient.invalidateQueries({
                queryKey: ["accountant-receipts-tray", userId],
              });
            });
        }
      }
    },
    [userId, setFiles],
  );
}

export function BulkUpload({
  userId,
  setFiles,
  uploadFile,
}: {
  userId: string;
  setFiles: React.Dispatch<React.SetStateAction<UploadedFile[]>>;
  // Passed in from ClientDetail so the tray can reuse the same uploader
  // for retries (keeps state updates going through one code path).
  uploadFile: (file: File, reuseTempId?: string) => Promise<void>;
}) {
  const [isDragging, setIsDragging] = useState(false);
  // Ensure userId participates in render deps (kept for parity with the
  // previous signature — the uploadFile closure already captures it).
  void userId;
  void setFiles;

  const handleFiles = useCallback(
    (fileList: FileList | File[]) => {
      // Two-bucket filter: passthrough (OpenAI accepts as-is) and convertible
      // (HEIC/HEIF — useUploadFile converts before upload). Anything else is
      // genuinely unsupported. Drag-and-drop bypasses <input accept>, so this
      // mirror filter is what catches a dropped TIFF/RAW/etc.
      const accepted: File[] = [];
      const rejected: File[] = [];
      for (const f of Array.from(fileList)) {
        if (PASSTHROUGH_RE.test(f.name) || CONVERTIBLE_RE.test(f.name)) {
          accepted.push(f);
        } else {
          rejected.push(f);
        }
      }
      if (rejected.length > 0) {
        toast.error(
          `${rejected.length} file${rejected.length > 1 ? "s were" : " was"} skipped. Only JPEG, PNG, WebP, GIF, PDF, or HEIC are accepted.`,
        );
      }
      accepted.forEach((f) => {
        void uploadFile(f);
      });
    },
    [uploadFile],
  );

  // Walk a DataTransferItemList for folder contents. Uses the
  // DataTransferItem.webkitGetAsEntry API which Chrome/Firefox/Safari
  // all support on drop events.
  const walkEntries = useCallback(
    async (items: DataTransferItemList): Promise<File[]> => {
      type FSEntry = {
        isFile: boolean;
        isDirectory: boolean;
        file?: (cb: (file: File) => void) => void;
        createReader?: () => {
          readEntries: (cb: (batch: FSEntry[]) => void) => void;
        };
      };
      const out: File[] = [];
      const readEntry = (entry: FSEntry | null | undefined): Promise<void> =>
        new Promise((resolve) => {
          if (!entry) return resolve();
          if (entry.isFile && entry.file) {
            entry.file((file) => {
              out.push(file);
              resolve();
            });
          } else if (entry.isDirectory && entry.createReader) {
            const reader = entry.createReader();
            const readAll = () => {
              reader.readEntries(async (batch) => {
                if (!batch.length) return resolve();
                await Promise.all(batch.map(readEntry));
                readAll();
              });
            };
            readAll();
          } else {
            resolve();
          }
        });

      const entries: FSEntry[] = [];
      for (const item of Array.from(items)) {
        const maybe = (
          item as DataTransferItem & {
            webkitGetAsEntry?: () => FSEntry | null;
          }
        ).webkitGetAsEntry?.();
        if (maybe) entries.push(maybe);
      }
      await Promise.all(entries.map(readEntry));
      return out;
    },
    [],
  );

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      // Prefer entries (folder support); fall back to flat files if unavailable.
      if (e.dataTransfer.items && e.dataTransfer.items.length) {
        const files = await walkEntries(e.dataTransfer.items);
        if (files.length) handleFiles(files);
        return;
      }
      if (e.dataTransfer.files.length) handleFiles(e.dataTransfer.files);
    },
    [handleFiles, walkEntries],
  );

  const pickFiles = (opts: { directory?: boolean } = {}) => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    if (opts.directory) {
      (input as HTMLInputElement & {
        webkitdirectory?: boolean;
      }).webkitdirectory = true;
    } else {
      input.accept = ACCEPT_ATTR;
    }
    input.onchange = () => {
      if (input.files) handleFiles(input.files);
    };
    input.click();
  };

  return (
    // A plain container, not a <button>: it holds the folder button below,
    // and a button inside a button is invalid HTML (browsers disagree on
    // which one a click lands on). The whole zone still opens the file
    // picker for the mouse; the headline is the real button for the keyboard
    // and for screen readers.
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={handleDrop}
      className={cn(
        "block w-full rounded-lg border-2 border-dashed p-12 text-center transition-colors cursor-pointer",
        isDragging
          ? "border-primary bg-primary/5"
          : "border-muted-foreground/25 hover:border-muted-foreground/50",
      )}
      onClick={() => pickFiles()}
    >
      <Upload
        className="mx-auto size-8 text-muted-foreground/50 mb-3"
        aria-hidden
      />
      <button
        type="button"
        className="block mx-auto text-sm font-medium cursor-pointer rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
        onClick={(e) => {
          e.stopPropagation();
          pickFiles();
        }}
      >
        Drag receipts or a folder here, or click to browse
      </button>
      <p className="text-xs text-muted-foreground mt-1">
        Accepts JPEG, PNG, WebP, GIF, HEIC, PDF. Drop a folder to upload all
        receipts inside.
      </p>
      <Button
        variant="link"
        size="sm"
        className="mt-2 h-auto p-0 text-xs"
        onClick={(e) => {
          e.stopPropagation();
          pickFiles({ directory: true });
        }}
      >
        Or pick a folder
      </Button>
    </div>
  );
}
